import type {SeriesOptionsType} from 'highcharts';
import CubeIndex from '../cube/CubeIndex';
import {percentAxisCap} from '../render/axisBounds';
import {itemColor} from '../render/colors';
import {
    axisLabelFormat,
    categoryLabel,
    computeDelta,
    countHtml,
    escapeHtml,
    formatValue,
    noDataHtml,
    previousRespondents,
} from '../render/format';
import {isLowBase} from '../render/lowBase';
import {MarkRenderer} from '../types';

const PREVIOUS_COLOR = '#9E9E9E';

const renderDumbbell: MarkRenderer = (cube, spec, ctx) => {
    const index = new CubeIndex(cube);
    const channel = typeof spec.encoding.x === 'string' ? spec.encoding.x : cube.dimensions[0]?.id;
    const measure = cube.measures[0];
    const color = itemColor(undefined, measure, 0, ctx);
    const previousName = escapeHtml(cube.meta.previousTerm?.label ?? ctx.labels?.previous ?? 'previous');
    const currentName = escapeHtml(cube.meta.term?.label ?? ctx.labels?.current ?? 'current');

    // Members lacking either end have nothing to connect, so they are dropped from the axis as well
    const rows = index.items(channel).flatMap(item => {
        // on a $measures axis every item is its own measure; the first one only styles the axis
        const rowMeasure = item.measure ?? measure;
        const cell = index.value(channel && !item.measure ? {[channel]: item.index} : {}, rowMeasure.id);
        if (!cell || cell.value === null || cell.previous === null || cell.previous === undefined) {
            return [];
        }
        return [{item, rowMeasure, cell: {...cell, value: cell.value}, previous: cell.previous}];
    });

    if (rows.length === 0) {
        return {type: 'html', render: el => { el.innerHTML = noDataHtml(ctx.labels?.noData ?? 'noData'); }};
    }

    const data = rows.map(({item, rowMeasure, cell, previous}) => {
        const lowBase = isLowBase(cube, cell);
        const delta = computeDelta(cell.value, previous, rowMeasure, ctx);
        const tooltip = `${escapeHtml(item.label)}<br>`
            + `<span style="color:${PREVIOUS_COLOR}">●</span> ${previousName}: `
            + `<b>${formatValue(previous, rowMeasure, ctx)}</b>, n=${previousRespondents(cell)}<br>`
            + `<span style="color:${color}">●</span> ${currentName}: <b>${formatValue(cell.value, rowMeasure, ctx)}</b>, `
            + countHtml(cell, lowBase)
            + (delta ? `<br>Δ <b>${delta.text}</b>` : '');

        return {
            name: escapeHtml(item.label),
            low: previous,
            high: cell.value,
            ...(lowBase ? {dashStyle: 'Dot', marker: {fillColor: ctx.dark ? '#1E1E1E' : '#FFFFFF'}} : {}),
            custom: {count: cell.count, lowBase, tooltip},
        };
    });

    // The dumbbell series has a single legend symbol, so two empty series stand in for previous/current
    const legendEntry = (id: string, name: string, markerColor: string): SeriesOptionsType => ({
        type: 'scatter',
        id,
        name,
        color: markerColor,
        data: [],
        enableMouseTracking: false,
        events: {legendItemClick: () => false},
    } as SeriesOptionsType);

    return {
        type: 'highcharts',
        options: {
            chart: {type: 'dumbbell'},
            title: {text: undefined},
            credits: {enabled: false},
            xAxis: {categories: rows.map(({item}) => categoryLabel(item))},
            yAxis: {
                title: {text: undefined},
                labels: {format: axisLabelFormat(measure)},
                ...percentAxisCap(
                    rows.map(row => row.rowMeasure),
                    rows.flatMap(row => [row.previous, row.cell.value]),
                ),
            },
            legend: {enabled: true},
            tooltip: {headerFormat: '', pointFormat: '{point.custom.tooltip}'},
            plotOptions: {
                series: {
                    dataLabels: {enabled: spec.options.dataLabels === true},
                },
            },
            series: [
                {
                    type: 'dumbbell',
                    id: measure.id,
                    name: escapeHtml(measure.label),
                    color,
                    lowColor: PREVIOUS_COLOR,
                    connectorColor: PREVIOUS_COLOR,
                    showInLegend: false,
                    data,
                } as SeriesOptionsType,
                legendEntry('__previous__', previousName, PREVIOUS_COLOR),
                legendEntry('__current__', currentName, color),
            ],
        },
    };
};

export default renderDumbbell;
